/**
 * Text-only editing for a cell whose markup must not be touched.
 *
 * WHY THIS EXISTS. A cell can hold six characters of copy inside four hundred of
 * chrome. The Access button in the PRO networking emails is exactly that:
 *
 *   <a href="https://…/pro-online" style="display:inline-block;background:#000000;
 *      color:#ffffff;font-family:Ubuntu,Helvetica,Arial,sans-serif;font-size:13px;…">Access</a>
 *
 * The rich text editor cannot open it — Tiptap keeps no inline style attribute, so the
 * button would lose its styling on the first save. But dropping the user into raw HTML
 * to change one word is not an answer either: it asks a copywriter to edit a styled
 * anchor by hand, and one stray character breaks the button in every inbox.
 *
 * So there is a third control. Extract the text that sits BETWEEN the tags, let the user
 * edit only that, and splice it back at the exact offsets. Every byte of markup is
 * preserved by construction, because the markup is never in the editor.
 *
 * CommonJS with a plain-node test, because this admin has no Jest, Vitest or RTL and
 * src/utils is the only place logic can be tested.
 */

// A run is a stretch of text between a '>' and the next '<'. Deliberately not a DOM
// parse: this runs per keystroke in the browser, the input is a fragment rather than a
// document, and offsets into the ORIGINAL string are what make the splice byte-exact.
// Anything inside a tag, a comment, or a <script>/<style> body is not a run.
const RUN_PATTERN = />([^<]+)</g;

// Text inside these never belongs to a copywriter.
const OPAQUE = /<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi;
const COMMENT = /<!--[\s\S]*?-->/g;

// A run worth offering. Whitespace-only runs are formatting between tags, and a run that
// is nothing but a Handlebars variable is a value the campaign supplies, not copy — both
// would only add noise to the panel.
const isEditableRun = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (/^\{\{\{?[^}]+\}\}\}?$/.test(trimmed)) return false;
  return true;
};

// Blank out spans we must not look inside, preserving length so offsets stay true.
const maskOpaque = (html) => {
  const blank = match => ' '.repeat(match.length);
  return html.replace(COMMENT, blank).replace(OPAQUE, blank);
};

/**
 * The editable text runs of a fragment, in document order.
 * Returns [{ index, start, end, text }] with offsets into the ORIGINAL html.
 */
function extractTextRuns(html) {
  if (!html || typeof html !== 'string') return [];

  const masked = maskOpaque(html);
  const runs = [];
  let match = RUN_PATTERN.exec(masked);

  while (match !== null) {
    const text = match[1];
    if (isEditableRun(text)) {
      const start = match.index + 1; // after the '>'
      runs.push({
        index: runs.length, start, end: start + text.length, text,
      });
    }
    match = RUN_PATTERN.exec(masked);
  }
  RUN_PATTERN.lastIndex = 0;

  return runs;
}

// The user is editing TEXT, so anything that would change the markup structure is
// escaped rather than honoured. Typing "<b>" produces the literal characters, not a tag.
// '&' first, or the escapes below would be double-escaped.
const escapeText = text => String(text)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

/**
 * Write one run back. Everything outside it is byte-identical by construction — the
 * splice is slice + text + slice, never a regex replace, so replacement patterns like
 * '$&' in the new text cannot be interpreted.
 *
 * Returns { ok, reason, html }. Refuses an unknown index rather than guessing.
 */
function replaceTextRun(html, runIndex, newText) {
  const runs = extractTextRuns(html);
  const run = runs[runIndex];

  if (!run) {
    return { ok: false, reason: `no editable text at position ${runIndex}`, html };
  }

  return {
    ok: true,
    reason: '',
    html: html.slice(0, run.start) + escapeText(newText) + html.slice(run.end),
  };
}

/**
 * Apply several runs at once, right to left so earlier offsets stay valid.
 * `edits` is { [runIndex]: newText }. Untouched runs are left exactly as they were.
 */
function replaceTextRuns(html, edits) {
  const runs = extractTextRuns(html);
  const indices = Object.keys(edits || {})
    .map(Number)
    .filter(i => runs[i])
    .sort((a, b) => b - a);

  let out = html;
  indices.forEach((i) => {
    const run = runs[i];
    out = out.slice(0, run.start) + escapeText(edits[i]) + out.slice(run.end);
  });

  return { ok: true, reason: '', html: out };
}

/** Is text-only editing worth offering? */
function hasEditableText(html) {
  return extractTextRuns(html).length > 0;
}

module.exports = {
  extractTextRuns, replaceTextRun, replaceTextRuns, hasEditableText, escapeText,
};
